import type { BuiltSvg, ViewName } from "../types";
import { HORIZONTAL_PADDING_PX, VERTICAL_PADDING_PX } from "../constant";
import { createBaseSvg, fitSvgToTightViewbox } from "../utils/svg";
import { drawDimensionInMillimeters } from "../drawing/dimensions";
import { drawRoomOutline } from "../drawing/room";
import { drawTopShelves } from "../drawing/shelvesTop";
import { drawSideShelves } from "../drawing/shelvesSide";

export const buildTopViewSvg = (
    roomLengthMm: number,
    roomDepthMm: number,
    sections: any[],
    pixelsPerMillimeter: number
): BuiltSvg => {
    const baseWidth = roomLengthMm * pixelsPerMillimeter + 2 * HORIZONTAL_PADDING_PX;
    const baseHeight = roomDepthMm * pixelsPerMillimeter + 2 * VERTICAL_PADDING_PX;
    const svg = createBaseSvg(baseWidth, baseHeight);

    const originXmm = HORIZONTAL_PADDING_PX / pixelsPerMillimeter;
    const originYmm = VERTICAL_PADDING_PX / pixelsPerMillimeter;

    drawDimensionInMillimeters(
        svg,
        originXmm,
        originYmm,
        originXmm + roomLengthMm,
        originYmm,
        12 / pixelsPerMillimeter,
        `${roomLengthMm}`,
        pixelsPerMillimeter
    );
    drawDimensionInMillimeters(
        svg,
        originXmm,
        originYmm + roomDepthMm,
        originXmm,
        originYmm,
        12 / pixelsPerMillimeter,
        `${roomDepthMm}`,
        pixelsPerMillimeter
    );
    drawDimensionInMillimeters(
        svg,
        originXmm + roomLengthMm,
        originYmm + roomDepthMm,
        originXmm,
        originYmm + roomDepthMm,
        -12 / pixelsPerMillimeter,
        `${roomLengthMm}`,
        pixelsPerMillimeter
    );
    drawDimensionInMillimeters(
        svg,
        originXmm + roomLengthMm,
        originYmm + roomDepthMm,
        originXmm + roomLengthMm,
        originYmm,
        -12 / pixelsPerMillimeter,
        `${roomDepthMm}`,
        pixelsPerMillimeter
    );

    drawRoomOutline(svg, roomLengthMm, roomDepthMm, pixelsPerMillimeter);
    drawTopShelves(svg, pixelsPerMillimeter, roomLengthMm, roomDepthMm, sections);

    fitSvgToTightViewbox(svg, 3);
    return { svg: svg.svg(), wPx: svg.width() as number, hPx: svg.height() as number };
};

export const buildSideViewSvg = (
    wallWidthMm: number,
    wallHeightMm: number,
    sections: any[],
    pixelsPerMillimeter: number,
    projectedFrom: { front?: any[]; back?: any[] } | undefined,
    viewName: "front" | "back" | "left" | "right" = "front",
    legendIndexByName?: Map<string, number>
): BuiltSvg => {
    const baseWidth = wallWidthMm * pixelsPerMillimeter + 2 * HORIZONTAL_PADDING_PX;
    const baseHeight = wallHeightMm * pixelsPerMillimeter + 2 * VERTICAL_PADDING_PX;
    const svg = createBaseSvg(baseWidth, baseHeight);

    drawSideShelves(
        svg,
        pixelsPerMillimeter,
        wallWidthMm,
        wallHeightMm,
        sections,
        projectedFrom,
        viewName,
        legendIndexByName
    );

    fitSvgToTightViewbox(svg, 3);
    return { svg: svg.svg(), wPx: svg.width() as number, hPx: svg.height() as number };
};

export const buildAllViewsSvg = (
    roomLengthMm: number,
    roomDepthMm: number,
    roomHeightMm: number,
    sections: any[],
    pixelsPerMillimeter: number
): Record<ViewName, BuiltSvg> => {
    const frontSections = sections.filter((s: any) => s.side === "front");
    const backSections = sections.filter((s: any) => s.side === "back");
    const leftSections = sections.filter((s: any) => s.side === "left");
    const rightSections = sections.filter((s: any) => s.side === "right");

    return {
        top: buildTopViewSvg(roomLengthMm, roomDepthMm, sections, pixelsPerMillimeter),
        front: buildSideViewSvg(roomLengthMm, roomHeightMm, frontSections, pixelsPerMillimeter, undefined, "front"),
        back: buildSideViewSvg(roomLengthMm, roomHeightMm, backSections, pixelsPerMillimeter, undefined, "back"),
        left: buildSideViewSvg(roomDepthMm, roomHeightMm, leftSections, pixelsPerMillimeter, undefined, "left"),
        right: buildSideViewSvg(roomDepthMm, roomHeightMm, rightSections, pixelsPerMillimeter, undefined, "right"),
    } as Record<ViewName, BuiltSvg>;
};
